import { useMemo, type ReactNode } from "react";

interface FieldProps {
  label: string;
  children: ReactNode;
  hint?: string;
  error?: string;
  className?: string;
}

export function Field({ label, children, hint, error, className }: FieldProps) {
  return (
    <div className={`flex flex-col gap-1.5 ${className ?? ""}`}>
      <label className="text-sm font-bold text-foreground">{label}</label>
      {children}
      {error ? (
        <span className="text-xs text-error">{error}</span>
      ) : hint ? (
        <span className="text-xs text-muted-foreground/80">{hint}</span>
      ) : null}
    </div>
  );
}

/**
 * Formata um valor canonico (ex: "300", "1200.5", "1200.50") como BRL "1.200,50".
 * Retorna "" para valores vazios/invalidos.
 */
function formatBRLDisplay(canonical: string): string {
  if (canonical === "" || canonical == null) return "";
  const num = parseFloat(canonical);
  if (!Number.isFinite(num)) return "";
  return num.toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/**
 * Input com mascara monetaria BRL (estilo "fintech").
 *
 * O usuario digita somente digitos; cada digito desloca a posicao do separador
 * decimal pra esquerda (modo "centavos"). Ex: digitando "1" -> "0,01",
 * "12345" -> "123,45", "1234500" -> "12.345,00".
 *
 * O `value` armazenado pelo pai e sempre na forma canonica decimal ("0", "300",
 * "1200.50") — pronto para `normalizeMoney` antes de enviar pra API.
 */
export function MoneyInput({
  value,
  onChange,
  placeholder = "R$ 0,00",
  disabled,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  disabled?: boolean;
}) {
  const display = useMemo(() => formatBRLDisplay(value), [value]);

  function handleChange(raw: string) {
    const digits = raw.replace(/\D/g, "");
    if (!digits) {
      onChange("");
      return;
    }
    // Cada digito representa 1 centavo. Limita a 15 digitos pra evitar overflow.
    const cents = parseInt(digits.slice(0, 15), 10);
    const reais = cents / 100;
    onChange(reais.toFixed(2));
  }

  return (
    <div className="relative">
      <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
        R$
      </span>
      <input
        type="text"
        inputMode="numeric"
        disabled={disabled}
        value={display}
        onChange={(e) => handleChange(e.target.value)}
        placeholder={placeholder.replace("R$ ", "")}
        className="h-11 w-full rounded-lg border border-input bg-card pl-9 pr-3 text-sm text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
      />
    </div>
  );
}

/**
 * Input com mascara percentual estilo "fintech" (mesma logica do MoneyInput).
 *
 * Digitos representam centesimos: "5" -> "0,05", "500" -> "5,00",
 * "12345" -> "123,45". Sufixo "%" fixo a direita. Valor armazenado pelo pai
 * sempre na forma canonica decimal ("5.00").
 */
export function PercentInput({
  value,
  onChange,
  placeholder = "0,00",
  disabled,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  disabled?: boolean;
}) {
  const display = useMemo(() => formatBRLDisplay(value), [value]);

  function handleChange(raw: string) {
    const digits = raw.replace(/\D/g, "");
    if (!digits) {
      onChange("");
      return;
    }
    const hundredths = parseInt(digits.slice(0, 10), 10);
    onChange((hundredths / 100).toFixed(2));
  }

  return (
    <div className="relative">
      <input
        type="text"
        inputMode="numeric"
        disabled={disabled}
        value={display}
        onChange={(e) => handleChange(e.target.value)}
        placeholder={placeholder}
        className="h-11 w-full rounded-lg border border-input bg-card px-3 pr-9 text-sm text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
      />
      <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
        %
      </span>
    </div>
  );
}

export function NumberInput({
  value,
  onChange,
  min = 0,
  suffix,
  disabled,
}: {
  value: string;
  onChange: (v: string) => void;
  min?: number;
  suffix?: string;
  disabled?: boolean;
}) {
  return (
    <div className="relative">
      <input
        type="number"
        min={min}
        disabled={disabled}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-11 w-full rounded-lg border border-input bg-card px-3 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
      />
      {suffix ? (
        <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
          {suffix}
        </span>
      ) : null}
    </div>
  );
}

export function Select<T extends string>({
  value,
  onChange,
  options,
  disabled,
}: {
  value: T;
  onChange: (v: T) => void;
  options: { value: T; label: string }[];
  disabled?: boolean;
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value as T)}
      disabled={disabled}
      className="h-11 w-full appearance-none rounded-lg border border-input bg-card px-3 pr-9 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50 bg-[url('data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 width=%2212%22 height=%2212%22 viewBox=%220 0 24 24%22 fill=%22none%22 stroke=%22%236B7280%22 stroke-width=%222%22><path d=%22M6 9l6 6 6-6%22/></svg>')] bg-[length:14px] bg-[position:right_12px_center] bg-no-repeat"
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}
